import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tabs, Tab, Typography } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeMultipleApiActions } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonFormFields from './PersonFormFields';
import PersonNamesTab from './PersonNamesTab';
import PersonContactMethodsTab from './PersonContactMethodsTab';
import PersonCredentialsTab from './PersonCredentialsTab';
import PersonEmploymentTab from './PersonEmploymentTab';
import PersonFeaturesTab from './PersonFeaturesTab';
import PersonDnaSamplesTab from './PersonDnaSamplesTab';
import PersonAssociatesTab from './PersonAssociatesTab';
import PersonVehicleRegistrationsTab from './PersonVehicleRegistrationsTab';
import PersonAddressesTab from './PersonAddressesTab';
import PersonTagsTab from './PersonTagsTab';
import PersonAttachmentsTab from './PersonAttachmentsTab';
import PersonPhotoTab from './PersonPhotoTab';
import PersonSmtRecordsTab from './PersonSmtRecordsTab';
import PersonBulletinsTab from './PersonBulletinsTab';
import PersonRelatedRecordsTab from './PersonRelatedRecordsTab';
import PersonSopsTab from './PersonSopsTab';
import PersonInfoSummaryCard from './PersonInfoSummaryCard';
import PersonTagsSummaryCard from './PersonTagsSummaryCard';
import {
  getPersonDetails,
  getPersonImages,
  getPeopleAddresses,
  getPeopleContactMethods,
  getPeopleCredentials,
  getPeopleTags,
  getPeopleSops,
  getPersonBulletins,
} from '../../services/userService';
import { PERSON_TABS, PERSON_TAB_KEYS } from '../../constants/editPersonFormConstants';
import { CONTACT_METHOD_TYPE } from '../../constants/addPersonFormConstants';
import { CREDENTIAL_TYPE } from '../../constants/personCredentialFormConstants';
import { closeEditPerson, setNewCreatedPesonAsSelected } from 'reducers/PersonDialogsReducer';
import UrgentMessage from '../../../../components/RMSRecordsDialogs/EditPersonDialog/UrgentMessage';

const EditPersonDialog = ({ onPersonUpdated } = {}) => {
  const dispatch = useDispatch();

  // adjust this selector path to match your actual store shape
  const editPerson = useSelector((state) => state.dialogs.editPerson);

  const open = editPerson !== null && editPerson !== undefined;
  const personId = editPerson?.ptsPersonID ? editPerson.ptsPersonID : editPerson;
  const origin = editPerson?.origin;

  const [activeTab, setActiveTab] = useState(PERSON_TAB_KEYS.EDIT_PERSON);
  const [loading, setLoading] = useState(false);
  const [personSummary, setPersonSummary] = useState(null);
  const [summaryPhotos, setSummaryPhotos] = useState([]);
  const [summaryAddress, setSummaryAddress] = useState(null);
  const [summaryPhone, setSummaryPhone] = useState(null);
  const [summaryEmail, setSummaryEmail] = useState(null);
  const [summarySsn, setSummarySsn] = useState(null);
  const [summaryOln, setSummaryOln] = useState(null);
  const [summaryTags, setSummaryTags] = useState([]);
  const [autoOpenAddTag, setAutoOpenAddTag] = useState(false);
  const [sopsCount, setSopsCount] = useState(0);
  const [bulletinsCount, setBulletinsCount] = useState(0);
  const [personFormState, setPersonFormState] = useState({
    canSave: true,
    submitting: false,
    isEditMode: false,
    recordCreated: null,
    recordCreatedBy: null,
    recordUpdated: null,
    recordUpdatedBy: null,
  });
  const personFormRef = useRef(null);
  const photoTabRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const [urgentMsgRefreshToken, setUrgentMsgRefreshToken] = useState(0);

  const loadSummary = useCallback(() => {
    if (!open || !personId) return;

    setUrgentMsgRefreshToken((prev) => prev + 1); // To reload UrgentMessage

    executeMultipleApiActions({
      setLoading,
      dispatch,
      actions: [
        {
          action: () => getPersonDetails(personId),
          onSuccess: ([person]) => setPersonSummary(person || null),
        },
        {
          action: () => getPersonImages(personId),
          onSuccess: (images) => setSummaryPhotos((images || []).filter((image) => !image.IsMark)),
        },
        {
          action: () => getPeopleAddresses(personId),
          onSuccess: (addresses) =>
            setSummaryAddress(addresses?.find((address) => address.IsPrimary) || null),
        },
        {
          action: () => getPeopleContactMethods(personId),
          onSuccess: (contacts) => {
            setSummaryPhone(
              contacts?.find((contact) => contact.ContactMethod === CONTACT_METHOD_TYPE.PHONE)
                ?.Info || null
            );
            setSummaryEmail(
              contacts?.find((contact) => contact.ContactMethod === CONTACT_METHOD_TYPE.EMAIL)
                ?.Info || null
            );
          },
        },
        {
          action: () => getPeopleCredentials(personId),
          onSuccess: (credentials) => {
            setSummarySsn(
              credentials?.find((credential) => credential.Type === CREDENTIAL_TYPE.SSN)?.Number ||
                null
            );
            setSummaryOln(
              credentials?.find((credential) => credential.Type === CREDENTIAL_TYPE.OLN)?.Number ||
                null
            );
          },
        },
        {
          action: () => getPeopleTags(personId),
          onSuccess: (tags) => setSummaryTags(tags || []),
        },
        {
          action: () => getPeopleSops(personId),
          onSuccess: (sopLinks) =>
            setSopsCount((sopLinks || []).filter((link) => !link.IsDeleted).length),
        },
        {
          action: () => getPersonBulletins(personId),
          onSuccess: (bulletins) =>
            setBulletinsCount((bulletins || []).filter((bulletin) => !bulletin.IsDeleted).length),
        },
      ],
    });
  }, [open, personId, dispatch]);

  const isActiveTabDirty = useCallback(() => {
    if (activeTab === PERSON_TAB_KEYS.EDIT_PERSON) {
      return Boolean(personFormRef.current?.isDirty?.());
    }
    if (activeTab === PERSON_TAB_KEYS.PHOTO) {
      return Boolean(photoTabRef.current?.isDirty?.());
    }
    return false;
  }, [activeTab]);

  const requestTabChange = useCallback(
    (nextTab, onSwitched) => {
      if (nextTab === activeTab) return;

      if (isActiveTabDirty()) {
        requestConfirm(
          'You have unsaved changes on this tab. Are you sure you want to leave without saving?',
          () => {
            setActiveTab(nextTab);
            onSwitched?.();
          },
          'Unsaved Changes'
        );
        return;
      }

      setActiveTab(nextTab);
      onSwitched?.();
    },
    [activeTab, isActiveTabDirty, requestConfirm]
  );

  const requestClose = useCallback(() => {
    const doClose = () => {
      dispatch(closeEditPerson());
      if (origin === 'party') {
        dispatch(setNewCreatedPesonAsSelected(personId));
      }
    };

    if (isActiveTabDirty()) {
      requestConfirm(
        'You have unsaved changes on this tab. Are you sure you want to close without saving?',
        doClose,
        'Unsaved Changes'
      );
      return;
    }

    doClose();
  }, [isActiveTabDirty, requestConfirm, dispatch, origin, personId]);

  const handlePersonFormStateChange = useCallback((state) => setPersonFormState(state), []);

  const handlePersonFormSave = useCallback(() => personFormRef.current?.submit(), []);

  const handleSaved = useCallback(
    (person) => {
      setPersonSummary(person);
      onPersonUpdated?.(person);
      loadSummary();
    },
    [onPersonUpdated, loadSummary]
  );

  const handleOpenPhotoTab = useCallback(() => requestTabChange(PERSON_TAB_KEYS.PHOTO), [
    requestTabChange,
  ]);

  const handleAddTag = useCallback(() => {
    if (activeTab !== PERSON_TAB_KEYS.TAGS) {
      requestTabChange(PERSON_TAB_KEYS.TAGS, () => setAutoOpenAddTag(true));
    } else {
      setAutoOpenAddTag(true);
    }
  }, [activeTab, requestTabChange]);

  const handleUrgentMsgItemClick = useCallback(
    (urgentMsg) => {
      const tabMap = {
        sop: PERSON_TAB_KEYS.SOPS,
        activeBulletins: PERSON_TAB_KEYS.BULLETINS,
      };
      const targetTab = tabMap[urgentMsg.key];
      if (targetTab) requestTabChange(targetTab);
    },
    [requestTabChange]
  );

  useEffect(() => {
    if (open) {
      loadSummary();
    } else {
      setActiveTab(PERSON_TAB_KEYS.EDIT_PERSON);
      setPersonSummary(null);
      setSummaryPhotos([]);
      setSummaryAddress(null);
      setSummaryPhone(null);
      setSummaryEmail(null);
      setSummarySsn(null);
      setSummaryOln(null);
      setSummaryTags([]);
      setSopsCount(0);
    }
  }, [open, loadSummary]);

  const activeTabLabel = PERSON_TABS.find((tab) => tab.key === activeTab)?.label;

  const renderTabLabel = (tab) => {
    if (tab.key === PERSON_TAB_KEYS.SOPS && sopsCount > 0) {
      return <span className="rms-tab-count-alert">{`${tab.label} (${sopsCount})`}</span>;
    }
    if (tab.key === PERSON_TAB_KEYS.BULLETINS && bulletinsCount > 0) {
      return <span className="rms-tab-count-alert">{`${tab.label} (${bulletinsCount})`}</span>;
    }

    return tab.label;
  };

  const handleAutoOpenAddTagHandled = useCallback(() => setAutoOpenAddTag(false), []);

  if (!open) return null;

  const tabContent = {
    [PERSON_TAB_KEYS.EDIT_PERSON]: (
      <PersonFormFields
        ref={personFormRef}
        open={open}
        personId={personId}
        onSaved={handleSaved}
        onLoadingChange={setLoading}
        onStateChange={handlePersonFormStateChange}
      />
    ),
    [PERSON_TAB_KEYS.NAMES]: <PersonNamesTab personId={personId} />,
    [PERSON_TAB_KEYS.CONTACT_METHODS]: <PersonContactMethodsTab personId={personId} />,
    [PERSON_TAB_KEYS.CREDENTIAL]: <PersonCredentialsTab personId={personId} />,
    [PERSON_TAB_KEYS.EMPLOYMENT]: <PersonEmploymentTab personId={personId} />,
    [PERSON_TAB_KEYS.FEATURES]: <PersonFeaturesTab personId={personId} />,
    [PERSON_TAB_KEYS.DNA_SAMPLES]: <PersonDnaSamplesTab personId={personId} />,
    [PERSON_TAB_KEYS.ASSOCIATE]: <PersonAssociatesTab personId={personId} />,
    [PERSON_TAB_KEYS.VEHICLE_REGISTRATIONS]: <PersonVehicleRegistrationsTab personId={personId} />,
    [PERSON_TAB_KEYS.ADDRESSES]: <PersonAddressesTab personId={personId} />,
    [PERSON_TAB_KEYS.TAGS]: (
      <PersonTagsTab
        personId={personId}
        autoOpenAdd={autoOpenAddTag}
        onAutoOpenAddHandled={handleAutoOpenAddTagHandled}
        onTagsChanged={loadSummary}
      />
    ),
    [PERSON_TAB_KEYS.ATTACHMENTS]: <PersonAttachmentsTab personId={personId} />,
    [PERSON_TAB_KEYS.PHOTO]: <PersonPhotoTab ref={photoTabRef} personId={personId} />,
    [PERSON_TAB_KEYS.SMT_RECORDS]: <PersonSmtRecordsTab personId={personId} />,
    [PERSON_TAB_KEYS.BULLETINS]: (
      <PersonBulletinsTab personId={personId} onBulletinsChanged={loadSummary} />
    ),
    [PERSON_TAB_KEYS.RELATED_RECORDS]: (
      <PersonRelatedRecordsTab personId={personId} onNavigateAway={requestClose} />
    ),
    [PERSON_TAB_KEYS.SOPS]: <PersonSopsTab personId={personId} onSopsChanged={loadSummary} />,
  };

  return (
    <RMSCommonDialog
      open={open}
      title={personSummary?.FullName ? `Edit Person - ${personSummary.FullName}` : 'Edit Person'}
      onClose={requestClose}
      onSave={handlePersonFormSave}
      loading={loading}
      maxWidth="xl"
      fullWidth
      closeOnBackdropClick={false}
      className="edit-person-dialog"
      saveButton={{
        show: activeTab === PERSON_TAB_KEYS.EDIT_PERSON,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      saveDisabled={personFormState.submitting || !personFormState.canSave}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        activeTab === PERSON_TAB_KEYS.EDIT_PERSON &&
        personFormState.isEditMode && (
          <RMSRecordHistoryButton
            disabled={!personFormState.recordCreated}
            created={personFormState.recordCreated}
            createdBy={personFormState.recordCreatedBy}
            updated={personFormState.recordUpdated}
            updatedBy={personFormState.recordUpdatedBy}
          />
        )
      }>
      <Box className="edit-person-dialog__summary">
        <Box className="edit-person-dialog__summary-info">
          <PersonInfoSummaryCard
            person={personSummary}
            photos={summaryPhotos}
            address={summaryAddress?.FullAddressText}
            phone={summaryPhone}
            email={summaryEmail}
            ssn={summarySsn}
            oln={summaryOln}
            onOpenPhotoTab={handleOpenPhotoTab}
          />
        </Box>
        <Box className="edit-person-dialog__summary-tags">
          <PersonTagsSummaryCard
            tags={summaryTags}
            onAddTag={handleAddTag}
            onTagDeleted={loadSummary}
          />
        </Box>
      </Box>

      <Box mb={1}>
        <UrgentMessage
          reloadUrgentMsg={urgentMsgRefreshToken}
          ptsPersonID={personId}
          onSopClick={handleUrgentMsgItemClick}
          onActiveBulletinsClick={handleUrgentMsgItemClick}
        />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => requestTabChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        className="edit-person-dialog__tabs">
        {PERSON_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={renderTabLabel(tab)} />
        ))}
      </Tabs>

      <Box mt={2}>
        {tabContent[activeTab] || (
          <Box p={4} display="flex" justifyContent="center">
            <Typography color="textSecondary">{activeTabLabel} — coming soon</Typography>
          </Box>
        )}
      </Box>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default EditPersonDialog;
